import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { CheckCircle2, Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { api, NetworkError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Resilient polling schedule (approx 60s total):
//   2s, 3s, 5s, 8s, 8s, 8s, 8s, 8s, 10s
const BACKOFF_SCHEDULE_MS = [2000, 3000, 5000, 8000, 8000, 8000, 8000, 8000, 10000];

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const navigate = useNavigate();
    const { refresh, user, loading: authLoading } = useAuth();

    const [status, setStatus] = useState("verifying");
    const [balance, setBalance] = useState(null);
    const [amount, setAmount] = useState(null);
    const [attempt, setAttempt] = useState(0);
    const [lastError, setLastError] = useState(null);
    const attemptsRef = useRef(0);
    const startingBalance = useRef(null);
    const timer = useRef(null);
    const firedConfetti = useRef(false);
    const cancelled = useRef(false);

    const finishSuccess = React.useCallback(
        (amt, bal) => {
            if (cancelled.current) return;
            setStatus("success");
            setAmount(amt ?? 1000);
            setBalance(bal ?? null);
            if (!firedConfetti.current) {
                firedConfetti.current = true;
                setTimeout(() => {
                    try {
                        confetti({
                            particleCount: 120,
                            spread: 80,
                            origin: { y: 0.35 },
                            colors: ["#FFFFFF", "#CCCCCC", "#CCCCCC"],
                        });
                    } catch {}
                }, 100);
            }
            refresh();
        },
        [refresh],
    );

    useEffect(() => {
        cancelled.current = false;
        return () => {
            cancelled.current = true;
            if (timer.current) clearTimeout(timer.current);
        };
    }, []);

    useEffect(() => {
        // Wait for auth session to be resolved before polling.
        if (authLoading) return;

        if (!sessionId) {
            setStatus("error");
            setLastError("missing_session_id");
            return;
        }

        if (!user) {
            // Session cookie lost during Stripe redirect (rare). Best-effort:
            // just show a friendly message instead of a raw fetch error.
            setStatus("unauth");
            return;
        }

        if (startingBalance.current === null) {
            startingBalance.current = user.credits ?? 0;
        }

        const verify = async () => {
            // Try subscription verify first; if the backend says it's a
            // credit-pack, fall back to the one-time verify.
            try {
                const sub = await api.verifySubscription(sessionId);
                if (sub.paid && sub.activated) {
                    return {
                        ok: true,
                        subscription: true,
                        planId: sub.plan_id,
                    };
                }
                if (sub.paid && !sub.activated) {
                    // Paid but subscription not yet materialised — treat as pending
                    return { ok: false, pending: true };
                }
                return { ok: false, pending: !sub.paid };
            } catch (e) {
                if (e.status === 400) {
                    // Not a subscription — do the credit-pack verify
                    const res = await api.verifyCheckout(sessionId);
                    return {
                        ok: res.paid && res.credited,
                        pending: res.paid && !res.credited,
                        subscription: false,
                        amount: res.amount,
                        balance: res.balance,
                    };
                }
                throw e;
            }
        };

        const poll = async () => {
            if (cancelled.current) return;
            attemptsRef.current += 1;
            setAttempt(attemptsRef.current);

            let networkFailed = false;
            try {
                const res = await verify();
                if (res.ok) {
                    if (res.subscription) {
                        await refresh();
                        finishSuccess(null, null);
                    } else {
                        finishSuccess(res.amount, res.balance);
                    }
                    return;
                }
            } catch (e) {
                if (e instanceof NetworkError) {
                    networkFailed = true;
                    setLastError("network");
                } else if (e.status === 401) {
                    setStatus("unauth");
                    return;
                } else if (e.status === 403) {
                    setStatus("error");
                    setLastError("forbidden");
                    return;
                } else if (e.status === 400) {
                    setStatus("error");
                    setLastError(e.message || "stripe_error");
                    return;
                } else {
                    networkFailed = true;
                    setLastError("server");
                }
            }

            // Fallback: check /auth/me — if balance jumped, the webhook already
            // credited the user. Treat that as success.
            if (!cancelled.current) {
                try {
                    const me = await api.me();
                    if (
                        startingBalance.current !== null &&
                        me &&
                        (me.credits ?? 0) > startingBalance.current
                    ) {
                        const delta = (me.credits ?? 0) - startingBalance.current;
                        finishSuccess(delta, me.credits);
                        return;
                    }
                } catch {
                    /* keep polling */
                }
            }

            if (
                attemptsRef.current >= BACKOFF_SCHEDULE_MS.length &&
                cancelled.current === false
            ) {
                setStatus(networkFailed ? "timeout_network" : "timeout");
                return;
            }

            const delay =
                BACKOFF_SCHEDULE_MS[
                    Math.min(
                        attemptsRef.current - 1,
                        BACKOFF_SCHEDULE_MS.length - 1,
                    )
                ];
            timer.current = setTimeout(poll, delay);
        };

        poll();

        return () => {
            if (timer.current) clearTimeout(timer.current);
        };
    }, [sessionId, authLoading, user, finishSuccess, refresh]);

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center px-6">
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className="w-full max-w-lg rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-8"
                data-testid="checkout-success-card"
            >
                {status === "verifying" && (
                    <>
                        <div className="h-10 w-10 rounded-full bg-[var(--color-panel-ai)] grid place-items-center">
                            <Loader2 className="h-5 w-5 animate-spin text-[var(--color-secondary)]" />
                        </div>
                        <h1
                            data-testid="checkout-verifying-title"
                            className="mt-5 text-2xl font-semibold tracking-tight"
                        >
                            Verifica del pagamento in corso…
                        </h1>
                        <p className="mt-2 text-sm text-text-secondary">
                            Stiamo confermando il tuo acquisto con Stripe. Di
                            solito ci vogliono pochi secondi.
                        </p>
                        <p
                            data-testid="checkout-attempt-count"
                            className="mt-4 text-xs text-text-secondary/70"
                        >
                            Tentativo {attempt} · {BACKOFF_SCHEDULE_MS.length}
                            {lastError === "network" &&
                                " · problema di rete, riprovo automaticamente"}
                        </p>
                    </>
                )}

                {status === "success" && (
                    <>
                        <div className="h-10 w-10 rounded-full bg-[var(--color-success)]/15 grid place-items-center">
                            <CheckCircle2 className="h-5 w-5 text-[var(--color-success)]" />
                        </div>
                        <h1
                            data-testid="checkout-success-title"
                            className="mt-5 text-2xl font-semibold tracking-tight"
                        >
                            {amount
                                ? `${amount} crediti accreditati!`
                                : "Piano attivato!"}
                        </h1>
                        <p className="mt-2 text-sm text-text-secondary">
                            {amount
                                ? `Il tuo saldo aggiornato è ${balance ?? "—"} crediti.`
                                : `Il tuo piano è ora attivo. Crediti mensili + moduli sbloccati.`}
                        </p>
                        <button
                            onClick={() => navigate("/dashboard")}
                            data-testid="checkout-success-continue"
                            className="mt-6 inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2.5 text-sm font-medium text-white transition-transform duration-150 hover:scale-[0.98]"
                        >
                            Vai alla dashboard
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    </>
                )}

                {(status === "timeout" || status === "timeout_network") && (
                    <>
                        <div className="h-10 w-10 rounded-full bg-[var(--color-panel-ai)] grid place-items-center">
                            <AlertTriangle className="h-5 w-5 text-[var(--color-secondary)]" />
                        </div>
                        <h1
                            data-testid="checkout-timeout-title"
                            className="mt-5 text-2xl font-semibold tracking-tight"
                        >
                            Verifica pagamento in corso…
                        </h1>
                        <p className="mt-2 text-sm text-text-secondary">
                            {status === "timeout_network"
                                ? "Non siamo riusciti a raggiungere il server per la conferma finale, ma se il pagamento è andato a buon fine i crediti verranno accreditati automaticamente dal webhook di Stripe."
                                : "Se non vedi il saldo aggiornarsi entro qualche minuto, il pagamento è comunque stato registrato correttamente e i crediti compariranno al prossimo login."}
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <button
                                onClick={() => navigate("/dashboard")}
                                data-testid="checkout-success-goto-dashboard"
                                className="inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2.5 text-sm font-medium text-white transition-transform duration-150 hover:scale-[0.98]"
                            >
                                Vai alla dashboard
                            </button>
                            <a
                                href="mailto:support@globalleadsuite.io"
                                data-testid="checkout-support-link"
                                className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-5 py-2.5 text-sm text-text-secondary hover:text-text-main transition-colors"
                            >
                                Contatta il supporto
                            </a>
                        </div>
                    </>
                )}

                {status === "unauth" && (
                    <>
                        <div className="h-10 w-10 rounded-full bg-[var(--color-panel-ai)] grid place-items-center">
                            <AlertTriangle className="h-5 w-5 text-[var(--color-secondary)]" />
                        </div>
                        <h1
                            data-testid="checkout-unauth-title"
                            className="mt-5 text-2xl font-semibold tracking-tight"
                        >
                            Sessione scaduta durante il pagamento
                        </h1>
                        <p className="mt-2 text-sm text-text-secondary">
                            Il pagamento è stato preso in carico da Stripe. Fai
                            di nuovo l'accesso: i crediti verranno accreditati
                            entro pochi secondi tramite il webhook.
                        </p>
                        <button
                            onClick={() => window.location.replace("/")}
                            data-testid="checkout-unauth-login"
                            className="mt-6 inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2.5 text-sm font-medium text-white transition-transform duration-150 hover:scale-[0.98]"
                        >
                            Torna al login
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    </>
                )}

                {status === "error" && (
                    <>
                        <div className="h-10 w-10 rounded-full bg-[var(--color-alert)]/15 grid place-items-center">
                            <AlertTriangle className="h-5 w-5 text-[var(--color-alert)]" />
                        </div>
                        <h1
                            data-testid="checkout-error-title"
                            className="mt-5 text-2xl font-semibold tracking-tight text-[var(--color-alert)]"
                        >
                            Impossibile verificare il pagamento
                        </h1>
                        <p className="mt-2 text-sm text-text-secondary">
                            {lastError === "missing_session_id"
                                ? "Manca l'identificativo della sessione Stripe. Torna alla dashboard e riprova l'acquisto."
                                : lastError === "forbidden"
                                  ? "Questa sessione Stripe appartiene a un altro account."
                                  : "Si è verificato un problema tecnico. Se il pagamento è andato a buon fine i crediti compariranno a breve."}
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <button
                                onClick={() => navigate("/dashboard")}
                                className="inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2.5 text-sm font-medium text-white transition-transform duration-150 hover:scale-[0.98]"
                                data-testid="checkout-error-dashboard"
                            >
                                Torna alla dashboard
                            </button>
                            <a
                                href="mailto:support@globalleadsuite.io"
                                data-testid="checkout-error-support"
                                className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-5 py-2.5 text-sm text-text-secondary hover:text-text-main transition-colors"
                            >
                                Contatta il supporto
                            </a>
                        </div>
                    </>
                )}
            </motion.div>
        </div>
    );
}
